import { ChatMessage } from './chatService';

export interface ChatHistoryEntry {
  messages: ChatMessage[];
  lastAccessed: number;
  expiresAt: number;
  proposalTitle?: string;
}

export interface ChatHistorySummary {
  proposalId: number;
  proposalTitle: string;
  messageCount: number;
  lastAccessed: number;
}

export interface ChatMemoryConfig {
  maxRetentionDays: number;
  maxMessagesPerProposal: number;
  cleanupIntervalHours: number;
}

const DEFAULT_CONFIG: ChatMemoryConfig = {
  maxRetentionDays: 2, // 2 dias de retenção
  maxMessagesPerProposal: 100, // máximo 100 mensagens por proposta
  cleanupIntervalHours: 24, // limpeza a cada 24 horas
};

const STORAGE_KEY_PREFIX = 'prainte_chat_history_';
const STORAGE_KEY_CONFIG = 'prainte_chat_memory_config';
const STORAGE_KEY_LAST_CLEANUP = 'prainte_chat_last_cleanup';

export class ChatMemoryService {
  private config: ChatMemoryConfig;

  constructor(config: Partial<ChatMemoryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.loadConfig();
  }

  private loadConfig(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_CONFIG);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.config = { ...this.config, ...parsed };
      }
    } catch (error) {
      console.warn('[ChatMemoryService] Erro ao carregar config:', error);
    }
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(this.config));
    } catch (error) {
      console.warn('[ChatMemoryService] Erro ao salvar config:', error);
    }
  }

  private getStorageKey(proposalId: number): string {
    return `${STORAGE_KEY_PREFIX}${proposalId}`;
  }

  private isExpired(entry: ChatHistoryEntry): boolean {
    return Date.now() > entry.expiresAt;
  }

  private cleanupExpired(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const entry: ChatHistoryEntry = JSON.parse(data);
            if (this.isExpired(entry)) {
              keysToRemove.push(key);
            }
          }
        } catch (error) {
          console.warn(`[ChatMemoryService] Erro ao verificar ${key}:`, error);
          keysToRemove.push(key); // Remove chaves corrompidas
        }
      }
    }

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[ChatMemoryService] Erro ao remover ${key}:`, error);
      }
    });

    if (keysToRemove.length > 0) {
      console.log(`[ChatMemoryService] Removidos ${keysToRemove.length} históricos expirados`);
    }
  }

  private shouldRunCleanup(): boolean {
    const lastCleanup = localStorage.getItem(STORAGE_KEY_LAST_CLEANUP);
    if (!lastCleanup) return true;

    const lastTime = parseInt(lastCleanup, 10);
    const hoursSinceLastCleanup = (Date.now() - lastTime) / (1000 * 60 * 60);
    
    return hoursSinceLastCleanup >= this.config.cleanupIntervalHours;
  }

  private updateLastCleanup(): void {
    localStorage.setItem(STORAGE_KEY_LAST_CLEANUP, Date.now().toString());
  }

  private limitMessages(messages: ChatMessage[]): ChatMessage[] {
    if (messages.length <= this.config.maxMessagesPerProposal) {
      return messages;
    }
    
    // Mantém as mensagens mais recentes
    return messages.slice(-this.config.maxMessagesPerProposal);
  }

  private compressMessages(messages: ChatMessage[]): ChatMessage[] {
    // Remove mensagens muito longas ou duplicadas
    const seen = new Set<string>();
    return messages.filter(msg => {
      const key = `${msg.role}:${msg.content.substring(0, 100)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /**
   * Salva o histórico de chat para uma proposta específica
   */
  saveHistory(proposalId: number, messages: ChatMessage[], proposalTitle?: string): void {
    try {
      // Executa limpeza se necessário
      if (this.shouldRunCleanup()) {
        this.cleanupExpired();
        this.updateLastCleanup();
      }

      const now = Date.now();
      const expiresAt = now + (this.config.maxRetentionDays * 24 * 60 * 60 * 1000);

      // Processa mensagens antes de salvar
      let processedMessages = this.compressMessages(messages);
      processedMessages = this.limitMessages(processedMessages);

      // Preserva título existente se novo não for fornecido
      let titleToSave = proposalTitle;
      if (!titleToSave) {
        try {
          const existing = localStorage.getItem(this.getStorageKey(proposalId));
          if (existing) {
            const parsed: ChatHistoryEntry = JSON.parse(existing);
            titleToSave = parsed.proposalTitle;
          }
        } catch { /* ignore */ }
      }

      const entry: ChatHistoryEntry = {
        messages: processedMessages,
        lastAccessed: now,
        expiresAt,
        ...(titleToSave ? { proposalTitle: titleToSave } : {}),
      };

      const storageKey = this.getStorageKey(proposalId);
      localStorage.setItem(storageKey, JSON.stringify(entry));
      
      console.log(`[ChatMemoryService] Histórico salvo para proposta ${proposalId} (${processedMessages.length} mensagens)`);
    } catch (error) {
      console.error('[ChatMemoryService] Erro ao salvar histórico:', error);
      
      // Se deu erro de espaço, tenta limpar e salvar novamente
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.cleanupExpired();
        try {
          localStorage.setItem(this.getStorageKey(proposalId), JSON.stringify({
            messages: messages.slice(-50), // Reduz ainda mais
            lastAccessed: Date.now(),
            expiresAt: Date.now() + (this.config.maxRetentionDays * 24 * 60 * 60 * 1000),
          }));
        } catch (retryError) {
          console.error('[ChatMemoryService] Falha ao salvar mesmo após limpeza:', retryError);
        }
      }
    }
  }

  /**
   * Carrega o histórico de chat para uma proposta específica
   */
  loadHistory(proposalId: number): ChatMessage[] | null {
    try {
      const storageKey = this.getStorageKey(proposalId);
      const data = localStorage.getItem(storageKey);
      
      if (!data) return null;

      const entry: ChatHistoryEntry = JSON.parse(data);

      // Verifica se expirou
      if (this.isExpired(entry)) {
        localStorage.removeItem(storageKey);
        return null;
      }

      // Atualiza último acesso
      entry.lastAccessed = Date.now();
      localStorage.setItem(storageKey, JSON.stringify(entry));

      console.log(`[ChatMemoryService] Histórico carregado para proposta ${proposalId} (${entry.messages.length} mensagens)`);
      return entry.messages;
    } catch (error) {
      console.error('[ChatMemoryService] Erro ao carregar histórico:', error);
      return null;
    }
  }

  /**
   * Verifica se existe histórico para uma proposta
   */
  hasHistory(proposalId: number): boolean {
    try {
      const storageKey = this.getStorageKey(proposalId);
      const data = localStorage.getItem(storageKey);
      
      if (!data) return false;

      const entry: ChatHistoryEntry = JSON.parse(data);
      return !this.isExpired(entry);
    } catch (error) {
      return false;
    }
  }

  /**
   * Remove o histórico de uma proposta específica
   */
  clearHistory(proposalId: number): void {
    try {
      const storageKey = this.getStorageKey(proposalId);
      localStorage.removeItem(storageKey);
      console.log(`[ChatMemoryService] Histórico removido para proposta ${proposalId}`);
    } catch (error) {
      console.error('[ChatMemoryService] Erro ao remover histórico:', error);
    }
  }

  /**
   * Limpa todo o histórico de chat
   */
  clearAllHistory(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`[ChatMemoryService] Removidos ${keysToRemove.length} históricos`);
    } catch (error) {
      console.error('[ChatMemoryService] Erro ao limpar todo histórico:', error);
    }
  }

  /**
   * Lista todos os históricos armazenados (não expirados), ordenados por acesso recente
   */
  listHistories(): ChatHistorySummary[] {
    const results: ChatHistorySummary[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(STORAGE_KEY_PREFIX)) continue;

      const idStr = key.slice(STORAGE_KEY_PREFIX.length);
      const proposalId = parseInt(idStr, 10);
      if (isNaN(proposalId)) continue;

      try {
        const data = localStorage.getItem(key);
        if (!data) continue;
        const entry: ChatHistoryEntry = JSON.parse(data);
        if (this.isExpired(entry)) continue;

        results.push({
          proposalId,
          proposalTitle: entry.proposalTitle || `Proposta #${proposalId}`,
          messageCount: entry.messages.length,
          lastAccessed: entry.lastAccessed,
        });
      } catch { /* ignore */ }
    }

    return results.sort((a, b) => b.lastAccessed - a.lastAccessed);
  }

  /**
   * Obtém estatísticas do uso de memória
   */
  getMemoryStats(): {
    totalProposals: number;
    totalMessages: number;
    oldestEntry: number | null;
    newestEntry: number | null;
    config: ChatMemoryConfig;
  } {
    let totalProposals = 0;
    let totalMessages = 0;
    let oldestEntry: number | null = null;
    let newestEntry: number | null = null;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const entry: ChatHistoryEntry = JSON.parse(data);
            if (!this.isExpired(entry)) {
              totalProposals++;
              totalMessages += entry.messages.length;
              
              if (oldestEntry === null || entry.lastAccessed < oldestEntry) {
                oldestEntry = entry.lastAccessed;
              }
              if (newestEntry === null || entry.lastAccessed > newestEntry) {
                newestEntry = entry.lastAccessed;
              }
            }
          }
        } catch (error) {
          // Ignora entradas corrompidas
        }
      }
    }

    return {
      totalProposals,
      totalMessages,
      oldestEntry,
      newestEntry,
      config: { ...this.config },
    };
  }

  /**
   * Atualiza a configuração
   */
  updateConfig(newConfig: Partial<ChatMemoryConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
  }
}

// Singleton instance
export const chatMemoryService = new ChatMemoryService();

export default chatMemoryService;